import { css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { PassValidationCompStatus, spinner } from '../../../common/constants/api-constants';
import store from '../../../store/store';
import { StoreState } from '../../../store/store.interface';
import { updateUserProfile } from '../../user-profile/thunks/update-user-profile.thunk';
import { getAnalyticsSafeData, selectUser } from '../../user-profile/user-profile.selectors';
import { MyAccountFormWrapper } from '../my-account-form-wrapper';
import { ariaLabelWithNewTab } from '../../../common/utils/a11y-util';
import { FORM_CONST } from '../../base-component/forms/form-base-component';
import emit from '../../../common/events';
import { getAppFlow, getUserActiveTab } from '../../user-profile/utils/user-profile.util';
import { UserProfile } from '../../user-profile/user-profile.interface';

interface PasswordError {
  lengthAndSpace: Boolean;
  tripple: Boolean;
  charCase: Boolean;
  number: Boolean;
  usernamepassword: Boolean;
}

const PASSWORD_ERROR_MESSAGE = {
  lengthAndSpace: '9 to 20 characters without spaces',
  charCase: '1 upper case and 1 lower case letter',
  number: '1 number',
  tripple: 'Cannot have three repeating characters',
  usernamepassword: 'Email address cannot exist in the password',
};

@customElement('mhecomm-password-field')
export class PasswordField extends connect(store)(MyAccountFormWrapper) {
  static styles = [css`
    .edit-password:focus {
      outline-color: var(--theme-gray-500) !important;
    }
  `];
  
  @state()
  private passwordError: PasswordError = {
    lengthAndSpace: true,
    tripple: true,
    charCase: true,
    number: true,
    usernamepassword: true,
  };

  @state()
  private showSubmitError = false;

  @state()
  private showPasswordError = false;

  @state()
  private showConfirmPasswordError = false;

  private email = '';
  protected validationEventList = ['blur', 'keyup'];

  private editPasswordButtonId = 'my-account-edit-password-button';
  private editPasswordButtonLabel = 'Edit';

  private updateUserPasswordButtonId = 'my-account-update-user-password-button';
  private updateUserPasswordButtonText = 'Save Changes';

  private cancelPasswordButtonId = 'my-account-cancel-password-button';
  private cancelPasswordButtonLabel = 'Cancel';

  private user: UserProfile | undefined;

  get newPasswordEl() {
    return this.shadowRoot?.querySelector("input[name='newPassword']") as HTMLInputElement;
  }

  get confirmPasswordEl() {
    return this.shadowRoot?.querySelector("input[name='confirmNewPassword']") as HTMLInputElement;
  }

  stateChanged(state: StoreState): void {
    super.stateChanged(state);
    this.user = selectUser(state);
    this.email = this.user?.email;
  }

  protected updated(_changedProperties: Map<string | number | symbol, unknown>): void {
    super.updated(_changedProperties);

    if (_changedProperties.has('editMode') && this.editMode) {
      this.showSubmitError = false;
      this.showPasswordError = false;
      this.showConfirmPasswordError = false;
      this.passwordError = {
        lengthAndSpace: true,
        tripple: true,
        charCase: true,
        number: true,
        usernamepassword: true,
      };
    }
  }

  loadIcon(icon: string, status: 'success' | 'failed') {
    return html`${this.uxtIconReady
      ? html`
          <uxt-icon
            icon="${icon}"
            size="sm"
            class=${status === 'success' ? 'text-success' : 'text-gray'}
            role="img"
            aria-live="polite"
            aria-label=${status === 'success' ? 'requirement fulfilled' : 'requirement unfulfilled'}
          ></uxt-icon>
        `
      : ''}`;
  }

  renderEditMode() {
    return html`
      <form class="needs-validationg-2" @submit=${this.submit} novalidate>
        <p class="fw-bold">Password</p>
        <ul class="bg-light border d-flex flex-wrap form-label fw-normal list-group mb-2 p-2 rounded w-fit-content">
          ${Object.keys(this.passwordError).map(
            (key) => html`
              <li class="list-group-item border-0 bg-transparent p-0 pe-2 w-fit-content">
                ${this.passwordError[key]
                  ? this.loadIcon('close-circle', 'failed')
                  : this.loadIcon('check-circle-fill', 'success')}
                ${PASSWORD_ERROR_MESSAGE[key]}
              </li>
            `
          )}
        </ul>
        <div class="row g-2">
          <div class="col-12 col-md">
            <label class="form-label visually-hidden" for="newPassword">Create New Password</label>
            <input
              class="form-control"
              id="newPassword"
              name="newPassword"
              @blur=${() => (this.showPasswordError = false)}
              type="password"
              placeholder="Create New Password"
              required
              autofocus
            />
          </div>
          <div class="col-12 col-md">
            <label class="form-label visually-hidden" for="confirmNewPassword">Confirm New Password</label>
            <input
              class="form-control"
              id="confirmNewPassword"
              name="confirmNewPassword"
              @blur=${() => setTimeout(() => (this.showConfirmPasswordError = false), 100)}
              type="password"
              placeholder="Confirm New Password"
              required
            />
          </div>
          <div class="col-12">
            ${this.showPasswordError
              ? html`<div class="alert alert-danger py-2 fs-6 mt-1" role="alert" aria-live="assertive">
                  <uxt-icon icon="exclamation-triangle" class="me-1"></uxt-icon><span class="visually-hidden">Error&nbsp;</span>Make sure your passwords match and all
                  password requirements are fulfilled.
                </div>`
              : nothing}
            ${this.showConfirmPasswordError
              ? html`<div class="alert alert-danger py-2 fs-6 mt-1" role="alert" aria-live="assertive">
                  <uxt-icon icon="exclamation-triangle" class="me-1"></uxt-icon><span class="visually-hidden">Error&nbsp;</span>The passwords you entered do not match.
                </div>`
              : nothing}
          </div>
          <div class="col-12 col-md-auto">
            <button
              id="${this.updateUserPasswordButtonId}"
              class="btn btn-primary w-100 w-md-auto"
              ?disabled=${ifDefined(this.isSubmitted || undefined)}
            >
              ${this.isSubmitted ? spinner({ size: 'sm' }) : this.updateUserPasswordButtonText}
            </button>
          </div>
          <div class="col-12 col-md-auto">
            <button
              type="button"
              @click=${() => this.onClickCancelPasswordButton()}
              ?disabled=${ifDefined(this.isSubmitted || undefined)}
              class="btn btn-outline-primary w-100 w-md-auto"
            >
              Cancel
            </button>
          </div>
          ${this.showSubmitError
            ? html`<div class="col-12">
                <div class="alert alert-danger py-2 fs-6 invalid-feedback d-block" role="alert" aria-live="polite">
                  <uxt-icon icon="exclamation-triangle" class="me-1"></uxt-icon> That didn’t seem to work... We’re sorry
                  for the inconvenience. Please try again, and if the problem persists reach out to
                  <a href="https://mh.my.site.com/CXG/s/ContactUs" target="_blank" aria-label="${ariaLabelWithNewTab('customer support')}">customer support</a>.
                </div>
              </div>`
            : nothing}
        </div>
      </form>
    `;
  }

  renderReadonlyMode() {
    return html`
      <div class="d-flex">
        <div class="flex-grow-1">
          <p class="bold">Password</p>
          <span class="fw-light">**********</span>
        </div>
        <div>
          ${this.isDefault()
            ? html` <button
                id="${this.editPasswordButtonId}"
                class="btn btn-light-gray edit-password"
                @click=${() => this.onClickEditPasswordButton()}
                aria-label="Edit Password"
              >
                ${this.editPasswordButtonLabel}
              </button>`
            : nothing}
        </div>
      </div>
    `;
  }
  onClickEditPasswordButton() {
    this.updateComponentStatus(PassValidationCompStatus.waiting);
    emit({
      element: this,
      name: 'click',
      detail: {
        id: this.editPasswordButtonId,
        label: this.editPasswordButtonLabel,
        comp: 'button',
        user: getAnalyticsSafeData(this.user!),
        appFlow: getAppFlow(),
        myAccountSection: getUserActiveTab(),
      },
      dataLayer: true,
    });
  }
  onClickCancelPasswordButton() {
    this.updateComponentStatus(PassValidationCompStatus.default);
    emit({
      element: this,
      name: 'click',
      detail: {
        id: this.cancelPasswordButtonId,
        label: this.cancelPasswordButtonLabel,
        comp: 'button',
        user: getAnalyticsSafeData(this.user!),
        appFlow: getAppFlow(),
        myAccountSection: getUserActiveTab(),
      },
      dataLayer: true,
    });
  }
  async submit(e) {
    super.submit(e);
    if (this.isSubmitted) {
      return;
    }

    if (!this.isNewPasswordValid() || !this.isPasswordMatching()) {
      this.showPasswordError = true;
      this.showConfirmPasswordError = false;
      return;
    } else {
      this.showPasswordError = false;
      this.showConfirmPasswordError = false;
    }

    if (await this.isFormValid()) {
      this.isSubmitted = true;
      this.showSubmitError = false;
      const userData = this.getRawValue();

      try {
        await store.dispatch(updateUserProfile(userData)).unwrap();
        emit({
          element: this,
          name: 'submit',
          detail: {
            id: this.updateUserPasswordButtonId,
            label: this.updateUserPasswordButtonText,
            comp: 'button',
            user: getAnalyticsSafeData(this.user!),
            appFlow: getAppFlow(),
            myAccountSection: getUserActiveTab(),
          },
          dataLayer: true,
        });
      } catch (e) {
        console.error(e);
        this.showSubmitError = true;
      } finally {
        this.isSubmitted = false;
      }
    }
  }

  isNewPasswordValid() {
    this.passwordError = passwordValidator(this.newPasswordEl, this.email);
    const passwordErrorValues = Object.values(this.passwordError);
    return passwordErrorValues.every((bool) => !bool);
  }

  isPasswordMatching() {
    return (
      this.confirmPasswordEl.value &&
      this.newPasswordEl.value &&
      this.confirmPasswordEl.value === this.newPasswordEl.value
    );
  }

  protected validationMessage() {
    return {
      newPassword: 'Please enter your password.',
      confirmNewPassword: 'Please re-enter your password.',
    };
  }

  protected customValidator() {
    return {
      confirmNewPassword: () => {
        if (this.isPasswordMatching()) {
          this.showConfirmPasswordError = false;
          return false;
        } else {
          if(this.showPasswordError === false) {
            this.showConfirmPasswordError = true;
          }
          return FORM_CONST.hideErrorMessage;
        }
      },
      newPassword: () => {
        return this.isNewPasswordValid() ? false : FORM_CONST.hideErrorMessage;
      },
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-password-field': PasswordField;
  }
}

function passwordValidator(inputEl: HTMLInputElement, email: string) {
  // this regex check for atleast a number, an
  const charCase = /^(?=.*[A-Z])(?=.*[a-z]).+$/;
  const aNumber = /^(?=.*\d).+$/;
  const hasTripple = /(.)\1\1/;

  const password: string = inputEl.value;
  const userName = email.split('@')[0];
  const errors: PasswordError = {
    lengthAndSpace: false,
    tripple: false,
    charCase: false,
    number: false,
    usernamepassword: false,
  };

  if (password.length <= 8 || password.length > 20 || password.includes(' ')) {
    errors.lengthAndSpace = true;
  }
  if (!password || hasTripple.test(password)) {
    errors.tripple = true;
  }
  if (!password || (userName && password.includes(userName))) {
    errors.usernamepassword = true;
  }
  if (!charCase.test(password)) {
    errors.charCase = true;
  }
  if (!aNumber.test(password)) {
    errors.number = true;
  }

  return errors;
}
